import {useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';
import type {
  InspectionScheduleRequest,
  InspectionScheduleResponse,
} from 'src/api/ProactApiClient';
import {InspectionScheduleFormModal} from 'src/app/inspections/schedules/InspectionScheduleFormModal';
import {cadenceLabel} from 'src/app/inspections/schedules/inspectionScheduleHelpers';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  useCreateInspectionScheduleMutation,
  useDeleteInspectionScheduleMutation,
  useUpdateInspectionScheduleMutation,
} from 'src/redux/queries/inspectionSchedule';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface UseInspectionScheduleCrudOptions {
  canManage: boolean;
  /** Pins the form to one site (site-view surface); omit for the global tab. */
  siteId?: number;
}

export interface UseInspectionScheduleCrudResult {
  openCreate: () => void;
  openEdit: (schedule: InspectionScheduleResponse) => void;
  requestDelete: (schedule: InspectionScheduleResponse) => void;
  /** Form modal + delete confirm; render outside the table. */
  dialogs: React.ReactNode;
}

/**
 * Create/edit/delete flow for an inspection-schedule table.
 * @registry Form modal, delete confirm and mutation wiring for inspection schedules.
 */
export const useInspectionScheduleCrud = ({
  canManage,
  siteId,
}: UseInspectionScheduleCrudOptions): UseInspectionScheduleCrudResult => {
  const {setAlert} = useAlert();

  const [formOpened, formHandlers] = useDisclosure(false);
  const [editing, setEditing] = useState<InspectionScheduleResponse>();
  const [deleteTarget, setDeleteTarget] = useState<InspectionScheduleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const [createSchedule, {isLoading: isCreating}] =
    useCreateInspectionScheduleMutation();
  const [updateSchedule, {isLoading: isUpdating}] =
    useUpdateInspectionScheduleMutation();
  const [deleteSchedule] = useDeleteInspectionScheduleMutation();

  const openCreate = () => {
    setEditing(undefined);
    formHandlers.open();
  };

  const openEdit = (schedule: InspectionScheduleResponse) => {
    setEditing(schedule);
    formHandlers.open();
  };

  const closeForm = () => {
    formHandlers.close();
    setEditing(undefined);
  };

  const requestDelete = (schedule: InspectionScheduleResponse) => {
    setDeleteTarget(schedule);
    confirmDeleteHandlers.open();
  };

  const handleSave = async (request: InspectionScheduleRequest) => {
    try {
      if (request.id) {
        await updateSchedule(request).unwrap();
        setAlert('Schedule was successfully updated.', 'success');
      } else {
        await createSchedule(request).unwrap();
        setAlert('Schedule was successfully created.', 'success');
      }
      closeForm();
    } catch (err) {
      setAlert(parseErrorMessage(err), 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    confirmDeleteHandlers.close();
    try {
      await deleteSchedule(deleteTarget.id).unwrap();
      setAlert('Schedule was successfully deleted.', 'success');
    } catch (err) {
      setAlert(parseErrorMessage(err), 'error');
    }
    setDeleteTarget(undefined);
  };

  const dialogs = canManage ? (
    <>
      <InspectionScheduleFormModal
        opened={formOpened}
        onClose={closeForm}
        siteId={siteId}
        editingSchedule={editing}
        isSaving={isCreating || isUpdating}
        onSave={handleSave}
      />
      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Delete schedule?"
        body={
          deleteTarget
            ? `Delete the ${cadenceLabel(deleteTarget)} schedule for ${deleteTarget.inspectionTypeName}? This stops future generation.`
            : ''
        }
      />
    </>
  ) : null;

  return {openCreate, openEdit, requestDelete, dialogs};
};
